/**
 * Admin Collections — drops / capsules / curated product sets.
 * Mirrors the look + feel of Admin Categories with an extra product picker.
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatPrice } from "@/lib/api";
import { Plus, Save, Trash2, Loader2, Layers, Search, X, Check } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "@/components/admin/ImageUploader";
import { ExportButtons } from "@/lib/exportData";
import Pagination, { usePagination } from "@/components/admin/Pagination";

const BLANK = {
    _new: true,
    title: "",
    slug: "",
    subtitle: "",
    description: "",
    image: "",
    is_active: true,
    is_featured: false,
    sort_order: 0,
    product_ids: [],
    product_count: 0,
};

function ProductPicker({ selectedIds, onChange }) {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    useEffect(() => {
        api.get("/admin/products")
            .then((r) => setProducts(r.data || []))
            .finally(() => setLoading(false));
    }, []);
    const filtered = useMemo(() => {
        if (!q) return products;
        const lower = q.toLowerCase();
        return products.filter(
            (p) =>
                (p.title || "").toLowerCase().includes(lower) ||
                (p.slug || "").toLowerCase().includes(lower) ||
                (p.category || "").toLowerCase().includes(lower)
        );
    }, [products, q]);
    const set = new Set(selectedIds || []);
    const toggle = (id) => {
        const next = new Set(set);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        onChange(Array.from(next));
    };
    return (
        <div className="space-y-2" data-testid="collection-product-picker">
            <div className="relative">
                <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#857C72]" />
                <input
                    placeholder="Search products by name / slug / category"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 rounded-full bg-[#FFFCF7] border border-[#E7DED2] text-xs"
                />
            </div>
            <div className="max-h-72 overflow-y-auto rounded-2xl border border-[#E7DED2] bg-white divide-y divide-[#F4EFE6]">
                {loading ? (
                    <div className="p-4 text-xs text-[#857C72] flex items-center gap-2">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading products…
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="p-4 text-xs text-[#857C72]">No products match.</div>
                ) : (
                    filtered.map((p) => {
                        const checked = set.has(p.id);
                        return (
                            <button
                                key={p.id}
                                type="button"
                                onClick={() => toggle(p.id)}
                                className={`flex items-center gap-3 w-full text-left px-3 py-2 hover:bg-[#FFFCF7] transition-colors ${
                                    checked ? "bg-[#FFFCF7]" : ""
                                }`}
                                data-testid={`collection-pick-${p.slug}`}
                            >
                                <div className={`h-5 w-5 rounded-md border flex items-center justify-center flex-shrink-0 ${
                                    checked ? "bg-[#1B1B1F] border-[#1B1B1F] text-white" : "border-[#E7DED2] bg-white"
                                }`}>
                                    {checked && <Check className="h-3 w-3" />}
                                </div>
                                <img
                                    src={p.images?.[0]}
                                    alt=""
                                    className="h-9 w-9 rounded-lg object-cover bg-[#F4EFE6]"
                                />
                                <div className="min-w-0 flex-1">
                                    <p className="text-xs font-medium truncate">{p.title}</p>
                                    <p className="text-[10px] text-[#857C72] font-mono-ui truncate">
                                        {p.category} · {formatPrice(p.price)}
                                    </p>
                                </div>
                            </button>
                        );
                    })
                )}
            </div>
            <p className="text-[10px] text-[#857C72] font-mono-ui">
                {set.size} product{set.size === 1 ? "" : "s"} selected
            </p>
        </div>
    );
}

export default function AdminCollections() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");
    const [openIdx, setOpenIdx] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/collections");
            setItems(data || []);
        } catch {
            toast.error("Failed to load collections");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const update = (idx, patch) =>
        setItems((curr) => curr.map((c, i) => (i === idx ? { ...c, ...patch } : c)));

    const addBlank = () => {
        const fresh = { ...BLANK, _tempId: Math.random().toString(36).slice(2) };
        setItems([fresh, ...items]);
        setOpenIdx(0);
    };

    const save = async (cat, idx) => {
        try {
            const payload = {
                title: cat.title,
                slug: cat.slug || undefined,
                subtitle: cat.subtitle || "",
                description: cat.description || "",
                image: cat.image || "",
                is_active: !!cat.is_active,
                is_featured: !!cat.is_featured,
                sort_order: cat.sort_order || 0,
                product_ids: cat.product_ids || [],
            };
            if (cat._new) {
                const { data } = await api.post("/admin/collections", payload);
                setItems((curr) => curr.map((x, i) => (i === idx ? data : x)));
                toast.success("Collection created");
            } else {
                const { data } = await api.put(`/admin/collections/${cat.id}`, payload);
                setItems((curr) => curr.map((x) => (x.id === cat.id ? data : x)));
                toast.success("Collection saved");
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        }
    };

    const remove = async (cat) => {
        if (cat._new) {
            setItems((curr) => curr.filter((x) => x._tempId !== cat._tempId));
            return;
        }
        if (!window.confirm(`Delete collection "${cat.title}"?`)) return;
        try {
            await api.delete(`/admin/collections/${cat.id}`);
            setItems((curr) => curr.filter((x) => x.id !== cat.id));
            toast.success("Deleted");
        } catch {
            toast.error("Delete failed");
        }
    };

    const filtered = useMemo(
        () =>
            items.filter(
                (c) =>
                    !filter ||
                    (c.title || "").toLowerCase().includes(filter.toLowerCase()) ||
                    (c.slug || "").toLowerCase().includes(filter.toLowerCase())
            ),
        [items, filter]
    );
    const { page, pageSize, pageItems, setPage, setPageSize } = usePagination(filtered, 8);

    return (
        <div className="space-y-6" data-testid="admin-collections">
            <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <p className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[#857C72]">Catalog</p>
                    <h1 className="font-display text-3xl sm:text-4xl mt-1">Collections</h1>
                    <p className="text-xs text-[#857C72] mt-1">
                        {items.length} collection{items.length === 1 ? "" : "s"} · group products into shoppable drops / capsules.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        onClick={addBlank}
                        className="dw-btn-primary inline-flex items-center gap-2 px-4 h-10 font-mono-ui text-xs"
                        data-testid="add-collection-btn"
                    >
                        <Plus className="h-4 w-4" /> New collection
                    </button>
                    <ExportButtons
                        rows={items}
                        filename="xplosiv_experience_collections"
                        columns={["title", "slug", "subtitle", "is_active", "is_featured", "product_count", "sort_order"]}
                    />
                </div>
            </header>

            <input
                type="text"
                placeholder="Filter by title or slug…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-4 py-2 rounded-full bg-white border border-[#E7DED2] text-sm max-w-xs"
                data-testid="collections-filter"
            />

            {loading ? (
                <div className="flex items-center justify-center py-10 text-[#857C72]">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
                </div>
            ) : pageItems.length === 0 ? (
                <div className="rounded-2xl border-2 border-dashed border-[#E7DED2] p-10 text-center text-[#857C72]">
                    <Layers className="h-8 w-8 mx-auto mb-3 opacity-60" />
                    No collections yet. Click <strong>New collection</strong> to start.
                </div>
            ) : (
                <div className="space-y-4">
                    {pageItems.map((c) => {
                        const idx = items.indexOf(c);
                        const isOpen = openIdx === idx;
                        return (
                            <article
                                key={c.id || c._tempId}
                                className="rounded-2xl border border-[#E7DED2] bg-white overflow-hidden"
                                data-testid={`collection-card-${c.slug || idx}`}
                            >
                                <button
                                    type="button"
                                    onClick={() => setOpenIdx(isOpen ? null : idx)}
                                    className="w-full flex items-center gap-4 px-5 py-4 hover:bg-[#FFFCF7] transition-colors text-left"
                                    data-testid={`collection-toggle-${idx}`}
                                >
                                    <div className="h-14 w-14 rounded-xl bg-[#F4EFE6] overflow-hidden flex-shrink-0">
                                        {c.image ? (
                                            <img src={c.image} alt="" className="h-full w-full object-cover" />
                                        ) : (
                                            <div className="h-full w-full flex items-center justify-center text-[#857C72]">
                                                <Layers className="h-5 w-5" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-display text-lg truncate">
                                            {c.title || <span className="text-[#857C72]">Untitled collection</span>}
                                        </p>
                                        <p className="text-[10px] font-mono-ui text-[#857C72] uppercase tracking-[0.15em] mt-0.5">
                                            {c.slug || "—"} · {(c.product_ids || []).length} products · {c.is_active ? "active" : "draft"}
                                            {c.is_featured ? " · featured" : ""}
                                        </p>
                                    </div>
                                    <span className="text-[10px] font-mono-ui text-[#857C72]">
                                        {isOpen ? "Collapse" : "Edit"}
                                    </span>
                                </button>
                                {isOpen && (
                                    <div className="px-5 pb-5 pt-1 border-t border-[#E7DED2] grid grid-cols-1 lg:grid-cols-2 gap-5">
                                        <div className="space-y-3">
                                            <div className="grid grid-cols-2 gap-3">
                                                <input
                                                    placeholder="Title"
                                                    value={c.title || ""}
                                                    onChange={(e) => update(idx, { title: e.target.value })}
                                                    className="px-4 py-2 rounded-full bg-[#FFFCF7] border border-[#E7DED2] text-sm font-display"
                                                    data-testid={`collection-title-${idx}`}
                                                />
                                                <input
                                                    placeholder="Slug (auto)"
                                                    value={c.slug || ""}
                                                    onChange={(e) => update(idx, { slug: e.target.value })}
                                                    className="px-4 py-2 rounded-full bg-[#FFFCF7] border border-[#E7DED2] text-sm font-mono-ui"
                                                />
                                            </div>
                                            <input
                                                placeholder="Subtitle / tagline"
                                                value={c.subtitle || ""}
                                                onChange={(e) => update(idx, { subtitle: e.target.value })}
                                                className="w-full px-4 py-2 rounded-full bg-[#FFFCF7] border border-[#E7DED2] text-sm"
                                            />
                                            <textarea
                                                placeholder="Description (shown on collection page)"
                                                value={c.description || ""}
                                                onChange={(e) => update(idx, { description: e.target.value })}
                                                className="w-full px-4 py-2 rounded-2xl bg-[#FFFCF7] border border-[#E7DED2] text-sm min-h-[80px]"
                                            />
                                            <ImageUploader
                                                value={c.image}
                                                onChange={(url) => update(idx, { image: url })}
                                                label="Collection cover"
                                            />
                                            <div className="grid grid-cols-3 gap-3 items-center">
                                                <input
                                                    type="number"
                                                    placeholder="Sort"
                                                    value={c.sort_order ?? 0}
                                                    onChange={(e) => update(idx, { sort_order: parseInt(e.target.value || "0", 10) })}
                                                    className="px-4 py-2 rounded-full bg-[#FFFCF7] border border-[#E7DED2] text-sm"
                                                />
                                                <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!c.is_active}
                                                        onChange={(e) => update(idx, { is_active: e.target.checked })}
                                                    />
                                                    Active
                                                </label>
                                                <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!c.is_featured}
                                                        onChange={(e) => update(idx, { is_featured: e.target.checked })}
                                                    />
                                                    Featured
                                                </label>
                                            </div>
                                            <div className="flex items-center gap-2 pt-2 border-t border-[#E7DED2]">
                                                <button
                                                    onClick={() => save(c, idx)}
                                                    className="dw-btn-primary inline-flex items-center gap-1.5 px-4 h-9 text-xs font-mono-ui"
                                                    data-testid={`collection-save-${idx}`}
                                                >
                                                    <Save className="h-3.5 w-3.5" /> Save
                                                </button>
                                                <button
                                                    onClick={() => remove(c)}
                                                    className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-white border border-[#E7DED2] hover:bg-red-50 hover:text-red-600 text-xs font-mono-ui"
                                                    data-testid={`collection-delete-${idx}`}
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" /> Delete
                                                </button>
                                                <button
                                                    onClick={() => setOpenIdx(null)}
                                                    className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-white border border-[#E7DED2] hover:bg-[#FFFCF7] text-xs font-mono-ui ml-auto"
                                                >
                                                    <X className="h-3.5 w-3.5" /> Close
                                                </button>
                                            </div>
                                        </div>
                                        <div>
                                            <p className="text-[11px] font-mono-ui tracking-[0.15em] uppercase text-[#857C72] mb-2">
                                                Products in this collection
                                            </p>
                                            <ProductPicker
                                                selectedIds={c.product_ids || []}
                                                onChange={(ids) => update(idx, { product_ids: ids })}
                                            />
                                        </div>
                                    </div>
                                )}
                            </article>
                        );
                    })}
                </div>
            )}
            <Pagination page={page} pageSize={pageSize} total={filtered.length} onPage={setPage} onPageSize={setPageSize} />
        </div>
    );
}
